import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Search } from 'lucide-react';
import { Checkbox } from '../ui/Checkbox';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Pagination from '../Pagination';
import { FetchUsers } from '../../api/General/account';
import { UserView } from '../../models/general';
import { cn } from '../../utils/cn';

type EmployeesListProps = {
    company?: any;
    onSelectionChange?: (users: UserView[]) => void;
    existingPayslipUserIds?: Set<string>;
};

const EmployeesList = ({ company, onSelectionChange, existingPayslipUserIds }: EmployeesListProps) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [employees, setEmployees] = useState<UserView[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [page, setPage] = useState(1);
    const [searchText, setSearchText] = useState('');
    const [branchIds, setBranchIds] = useState<number[]>([]);
    const [departmentIds, setDepartmentIds] = useState<number[]>([]);
    const PAGE_SIZE = 10;

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                if (!company?.id) return;
                const res = await FetchUsers('Company', company.id, false);
                if (res.errors?.length) {
                    console.error(res.errors);
                    return;
                }
                setEmployees(res.payload || []);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [company?.id]);

    // Derive filter options from loaded employees
    const branchOptions = useMemo(() => {
        const map = new Map<number, string>();
        employees.forEach(e => {
            if (e.branchId && e.branch?.name) map.set(e.branchId, e.branch.name);
        });
        return Array.from(map, ([value, label]) => ({ value, label }));
    }, [employees]);

    const departmentOptions = useMemo(() => {
        const map = new Map<number, string>();
        employees.forEach(e => {
            if (e.departmentId && e.department?.name) map.set(e.departmentId, e.department.name);
        });
        return Array.from(map, ([value, label]) => ({ value, label }));
    }, [employees]);

    // Filter pipeline: search -> branch -> department
    const filtered = useMemo(() => {
        const lowerSearch = searchText.trim().toLowerCase();
        return employees.filter(e => {
            const nameMatch = !lowerSearch || (e.fullName || '').toLowerCase().includes(lowerSearch);
            const branchMatch = !branchIds.length || branchIds.includes(e.branchId || 0);
            const deptMatch = !departmentIds.length || departmentIds.includes(e.departmentId || 0);
            return nameMatch && branchMatch && deptMatch;
        });
    }, [employees, searchText, branchIds, departmentIds]);

    // Reset page when filters change
    useEffect(() => { setPage(1); }, [searchText, branchIds, departmentIds]);

    // Notify parent on selection change
    useEffect(() => {
        if (onSelectionChange) {
            onSelectionChange(employees.filter(e => selectedIds.has(e.id)));
        }
    }, [selectedIds, employees, onSelectionChange]);

    const allChecked = filtered.length > 0 && filtered.every(e => selectedIds.has(e.id));
    const indeterminate = !allChecked && filtered.some(e => selectedIds.has(e.id));

    const toggleAll = () => {
        if (allChecked) {
            setSelectedIds(prev => {
                const next = new Set(prev);
                filtered.forEach(e => next.delete(e.id));
                return next;
            });
        } else {
            setSelectedIds(prev => {
                const next = new Set(prev);
                filtered.forEach(e => next.add(e.id));
                return next;
            });
        }
    };

    const toggleOne = (id: string, checked: boolean) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (checked) next.add(id); else next.delete(id);
            return next;
        });
    };

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paged = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    const COL_COUNT = 6;

    return (
        <div className="flex flex-col gap-3 w-full">
            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                    placeholder={t('Search employees...')}
                    value={searchText}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
                    className="pl-9 h-9"
                />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="min-w-[180px]">
                    <Select
                        multiple
                        searchable
                        clearable
                        placeholder={t('All branches')}
                        options={branchOptions}
                        value={branchIds}
                        onChange={(val: number[]) => setBranchIds(val)}
                    />
                </div>
                <div className="min-w-[180px]">
                    <Select
                        multiple
                        searchable
                        clearable
                        placeholder={t('All departments')}
                        options={departmentOptions}
                        value={departmentIds}
                        onChange={(val: number[]) => setDepartmentIds(val)}
                    />
                </div>
            </div>

            {/* Counts */}
            <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/5 text-primary px-2.5 py-0.5 text-xs font-medium">
                    {t('Selected')}: {selectedIds.size}
                </span>
                <span className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                    {t('Total')}: {filtered.length}
                </span>
            </div>

            {/* Table */}
            <div className="border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border bg-muted/50">
                                <th className="w-10 px-3 py-2">
                                    <Checkbox
                                        checked={allChecked}
                                        indeterminate={indeterminate}
                                        onChange={toggleAll}
                                        aria-label={t('Select all employees')}
                                    />
                                </th>
                                <th className="px-3 py-2 text-start font-medium text-muted-foreground">{t('Employee Name')}</th>
                                <th className="px-3 py-2 text-start font-medium text-muted-foreground">{t('Department')}</th>
                                <th className="px-3 py-2 text-start font-medium text-muted-foreground">{t('Branch')}</th>
                                <th className="px-3 py-2 text-start font-medium text-muted-foreground">{t('Position')}</th>
                                <th className="px-3 py-2 w-24"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={COL_COUNT} className="text-center py-8">
                                        <svg className="animate-spin h-5 w-5 mx-auto text-primary" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                    </td>
                                </tr>
                            )}
                            {!loading && !paged.length && (
                                <tr>
                                    <td colSpan={COL_COUNT} className="text-center py-8 text-muted-foreground">
                                        {t('No employees found')}
                                    </td>
                                </tr>
                            )}
                            {!loading && paged.map(e => {
                                const isSelected = selectedIds.has(e.id);
                                const hasPayslip = existingPayslipUserIds?.has(e.id);
                                return (
                                    <tr
                                        key={e.id}
                                        className={cn(
                                            'border-b border-border last:border-b-0 transition-colors hover:bg-muted/30',
                                            isSelected && 'bg-primary/5'
                                        )}
                                    >
                                        <td className="px-3 py-2">
                                            <Checkbox
                                                checked={isSelected}
                                                onChange={() => toggleOne(e.id, !isSelected)}
                                                aria-label={t('Select employee')}
                                            />
                                        </td>
                                        <td className="px-3 py-2 font-medium">{e.fullName}</td>
                                        <td className="px-3 py-2 text-muted-foreground">{e.department?.name || '\u2014'}</td>
                                        <td className="px-3 py-2 text-muted-foreground">{e.branch?.name || '\u2014'}</td>
                                        <td className="px-3 py-2 text-muted-foreground">{e.title || '\u2014'}</td>
                                        <td className="px-3 py-2">
                                            {hasPayslip && (
                                                <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 px-2 py-0.5 text-xs font-medium">
                                                    {t('Generated')}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className="flex justify-end">
                <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} showEdges={false} />
            </div>
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    company: state.user.company,
});
export default connect(mapStateToProps)(EmployeesList);
